import React, { useCallback, useState } from "react";
import {
  comparePose3dModes,
  fetchAllPose3dForTe,
} from "../../api/pose3dApi";
import Pose3DBiomechanicsPanel from "./Pose3DBiomechanicsPanel";

const MODE_LABELS = {
  mono_world: "Mono (world)",
  multi_triangulate: "Multi-cam",
  lidar_iphone: "iPhone LiDAR",
  strided_transformer: "StridedTransformer (STT)",
};

function fmtM(v) {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  return `${(Number(v) * 1000).toFixed(1)} mm`;
}

function TrustBadge({ trust }) {
  if (!trust) return null;
  const colors = {
    high: { bg: "#e6f4ea", fg: "#137333" },
    medium: { bg: "#fef7e0", fg: "#b06000" },
    low: { bg: "#fce8e6", fg: "#c5221f" },
    uncompared: { bg: "#e8f0fe", fg: "#1967d2" },
    unknown: { bg: "#f1f3f4", fg: "#5f6368" },
  };
  const c = colors[trust.level] || colors.unknown;
  const pa =
    trust.primary_pair?.pa_mpjpe_m != null
      ? ` · PA-MPJPE ${fmtM(trust.primary_pair.pa_mpjpe_m)}`
      : "";
  const aligned = trust.table_aligned ? " · table-aligned" : "";
  return (
    <span
      title={trust.label}
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        background: c.bg,
        color: c.fg,
      }}
    >
      {trust.label}
      {pa}
      {aligned}
    </span>
  );
}

/**
 * Compare pose3d methods available for an exercise (te_id).
 */
export default function Pose3DComparePanel({ teId, t = (k, d) => d }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [available, setAvailable] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [player, setPlayer] = useState("left");
  const [lidarBio, setLidarBio] = useState(null);

  const refresh = useCallback(async () => {
    if (!teId) return;
    setBusy(true);
    setError("");
    try {
      const data = await fetchAllPose3dForTe(teId);
      const byMode = data.results_by_mode || {};
      const modes = Object.keys(byMode);
      setAvailable(modes);
      const lidar = byMode.lidar_iphone;
      setLidarBio(lidar?.settings?.biomechanics || null);
      if (modes.length >= 2) {
        const cmp = await comparePose3dModes({
          teId,
          modes,
          player,
        });
        setComparison(cmp);
        if (cmp?.biomechanics) setLidarBio(cmp.biomechanics);
      } else {
        setComparison(null);
        if (modes.length < 2) {
          setError(
            t(
              "pose3dCompareNeedTwo",
              "Need at least two Pose3D modes for this clip (mono / multi / STT / LiDAR)."
            )
          );
        }
      }
    } catch (err) {
      setError(err?.message || "Compare failed");
      setComparison(null);
    } finally {
      setBusy(false);
    }
  }, [teId, player, t]);

  if (!teId) return null;

  return (
    <div className="pose3d-compare-panel" style={{ marginTop: 12 }}>
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <strong>{t("pose3dCompare", "Pose3D compare")}</strong>
        <select
          value={player}
          onChange={(e) => setPlayer(e.target.value)}
          disabled={busy}
        >
          <option value="left">Player A (left)</option>
          <option value="right">Player B (right)</option>
        </select>
        <button type="button" onClick={refresh} disabled={busy}>
          {busy
            ? t("pose3dComparing", "Comparing…")
            : t("pose3dRunCompare", "Compare modes")}
        </button>
        {available.length ? (
          <span style={{ opacity: 0.75, fontSize: 12 }}>
            {t("pose3dAvailable", "Available")}:{" "}
            {available.map((m) => MODE_LABELS[m] || m).join(", ")}
          </span>
        ) : null}
        <TrustBadge trust={comparison?.trust} />
      </div>
      {error ? (
        <div style={{ color: "#b00020", marginTop: 8, fontSize: 13 }}>
          {error}
        </div>
      ) : null}
      <Pose3DBiomechanicsPanel
        biomechanics={lidarBio || comparison?.biomechanics}
        t={t}
      />
      {comparison?.pairs?.length ? (
        <table
          style={{
            width: "100%",
            marginTop: 10,
            borderCollapse: "collapse",
            fontSize: 13,
          }}
        >
          <thead>
            <tr>
              <th align="left">Pair</th>
              <th align="right">Frames</th>
              <th align="right">MPJPE</th>
              <th align="right">PA-MPJPE</th>
              <th align="right">Cov A/B</th>
            </tr>
          </thead>
          <tbody>
            {comparison.pairs.map((p) => (
              <tr key={`${p.mode_a}-${p.mode_b}`}>
                <td>
                  {(MODE_LABELS[p.mode_a] || p.mode_a) +
                    " vs " +
                    (MODE_LABELS[p.mode_b] || p.mode_b)}
                </td>
                <td align="right">{p.aligned_frames}</td>
                <td align="right">{fmtM(p.mpjpe_m)}</td>
                <td align="right">{fmtM(p.mpjpe_procrustes_m)}</td>
                <td align="right">
                  {p.coverage_a != null
                    ? `${Math.round(p.coverage_a * 100)}%`
                    : "—"}
                  {" / "}
                  {p.coverage_b != null
                    ? `${Math.round(p.coverage_b * 100)}%`
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      <p style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>
        {t(
          "pose3dCompareHint",
          "Generate mono and/or multi on the server, and upload LiDAR from an iPhone Pro for the same exercise, then compare."
        )}
      </p>
    </div>
  );
}
